import { AssemblyPart } from "./assembly";
import { BuildInvoiceCyle } from "./build.invoice_cycle";
import { ArrayDate, BaseData } from "./_global";
import { DRIVE_FOLDER_URL } from "@dis/gapi";

export interface Build extends BaseData {
  buildNumber: string | null;
  serialNumber: string | null;
  assemblyNumber: string | null;
  jobUid: string | null;
  buildComponentList: BuildComponent[];
  statuses: BuildStatuses | null;
  assemblyCard: string | undefined;
  functionTestCard: string | null;
  folderId: string | null;
  toolType: string | null;
  dateOut: string | null;
  dateIn: string | null;
  notes: string | null;
  notesArray: any[];
  workFlowSteps: BuildWorkFlowStep[];
  metadata: BuildMetadata | null;

  // Deprecated fields
  loadout: Loadout;
  projectNumber: string | null;
  jobNumber: string | null;
  projectNumber_jobNumber: string | null;
  buildComponents: BuildComponent[];
  components: string;
  invoices: BuildInvoiceCyle[];
  buildDate: string | null;
  description: string | null;
  list: string | null;
}

export class Build extends BaseData {
  constructor(data: Partial<Build> = {}) {
    super(data);
    this.uid = data.buildNumber || null;
    this.buildNumber = data.buildNumber || data.uid || null;
    this.serialNumber = data.serialNumber || null;
    this.assemblyNumber = data.assemblyNumber || null;
    this.jobUid = data.jobUid || null;
    this.buildComponentList = data.buildComponentList || [];
    this.statuses = data.statuses || null;
    this.assemblyCard = data.assemblyCard;
    this.functionTestCard = data.functionTestCard || null;
    this.folderId = data.folderId || data.buildFolderId || null;
    this.toolType = data.toolType || null;
    this.notes = data.notes || null;
    this.notesArray = this.getBuildNotesArray(this.notes);
    this.dateOut = data.dateOut || null;
    this.dateIn = data.dateIn || null;
    this.workFlowSteps = data.workFlowSteps || [];
    this.metadata = data.metadata || null;

    /**
     * @deprecated not sure if even being used;
     */
    this.description = data.description || null;
    this.buildComponents = data.buildComponents || [];
    this.components = data.components || "";
    this.list = data.list || null;
    this.projectNumber = data.projectNumber || null;
    this.jobNumber = data.jobNumber || null;
    this.projectNumber_jobNumber = data.projectNumber_jobNumber || null;
    this.loadout = data.loadout || {
      projectNumber: data.projectNumber || null,
      jobNumber: data.jobNumber || null,
      shippingContainer: null,
      outDate: null,
      inDate: null,
      status: null,
    };
    this.invoices = [];
    this.buildDate = data.buildDate || null;

    this.fromSpreadsheet(data);
  }

  /**
   * @description Used for naming documents and folders, use this for consitenacy in naming
   */
  public get title() {
    return this.buildNumber;
  }

  /**
   * @deprecated use folderId instead;
   * Can probably be deleted
   */
  public get buildFolderId() {
    return this.folderId;
  }

  public get folderUrl() {
    return `${DRIVE_FOLDER_URL}${this.folderId}`;
  }

  private fromSpreadsheet(build: Partial<Build>) {
    if (typeof build.statuses === "string") {
      this.statuses = JSON.parse(build.statuses);
    }
    if (typeof build.buildComponentList === "string") {
      this.buildComponentList = JSON.parse(build.buildComponentList);
    }
    if (typeof build.workFlowSteps === "string") {
      this.workFlowSteps = JSON.parse(build.workFlowSteps);
    }
    if (typeof build.metadata === "string") {
      this.metadata = JSON.parse(build.metadata);
      /**
       * //TODO: remove this in the future;
       * @description temporary fix for interternal testing builds that have metadata as a string
       */
      if ("buildInternalTesting" in this.metadata!.booleanMetadata) {
        this.metadata!.booleanMetadata.internalTesting = this.metadata!.booleanMetadata.buildInternalTesting!;
        delete this.metadata!.booleanMetadata.buildInternalTesting;
      }
    }
  }

  static toSpreadsheet(build: Build) {
    //@ts-expect-error
    build.statuses = JSON.stringify(build.statuses);
    //@ts-expect-error
    build.buildComponentList = JSON.stringify(build.buildComponentList);
    //@ts-expect-error
    build.metadata = JSON.stringify(build.metadata);
    //@ts-expect-error
    build.workFlowSteps = JSON.stringify(build.workFlowSteps);
    return build;
  }

  private getBuildNotesArray(noteString: string | null): string[] {
    if (noteString) {
      const noteArray = noteString.split("_");
      return noteArray;
    } else {
      const noteArray: string[] = [];
      return noteArray;
    }
  }
}

export const stagedBuildIds = [
  "STAGED_BUILDS",
  "CHEVRON_CUSTOMER_INV",
  "SHELL_CUSTOMER_INV",
  "LLOG_CUSTOMER_INV",
  "EXXONMOBILE_CUSTOMER_INV",
  "BEACON_CUSTOMER_INV",
  "BP_CUSTOMER_INV",
  // Deprecated Staged Builds Ids
  "4c75e7a82fec401fbf537927b6d5e691", // Old Staged Builds Id
];
export interface BuildComponent {
  assemblyPart: AssemblyPart;
  selectedPart: string;
  selectedComponent: string | string[];
  selectedQty: number;
  multiBool: boolean;
  hasSoftChange?: boolean;
  serialNumberNotRequired?: boolean;

  // Deprecated property
  dressingOverride?: BuildComponentDressingOverrides;
}

export interface BuildWorkFlowStep {
  // Snapshot of Data from WorkFlow Definitions sheet
  name: string;
  displayName: string;
  description: string;
  sequenceOrder: number;
  durationEstimateInDays: number;
  // Build step specific properties
  tpiRequired: boolean;
  referenceFileId?: string; // File Id for any file relating to this step (i.e. Inspection)
  // Scheduling Dates
  startDate: Date | null;
  endDate: Date | null;
  // Step Completion
  completedAt: Date | null;
  completedBy: string;
}

export interface BuildComponentDressingOverrides {
  partNumberNotRequired: boolean;
  serialNumberNotRequired: boolean;
  buildComponentOverride: boolean;
}

export interface BuildMetadata {
  booleanMetadata: BuildBooleanMetadata;
  dateMetadata: BuildDateMetadata;
}

export interface BuildBooleanMetadata {
  approved: boolean;
  manufacturingRequired: boolean;
  manufacturingComplete: boolean;
  assemblyRequired: boolean;
  assemblyComplete: boolean;
  testingRequired: boolean;
  testingComplete: boolean;
  buckingRequired: boolean;
  buckingComplete: boolean;
  finalPrepRequired: boolean;
  finalPrepComplete: boolean;
  finalized: boolean;
  internalTesting: boolean;
  /**
   * @deprecated use internalTesting instead;
   */
  buildInternalTesting?: boolean;
  retest: boolean;
  reRelease: boolean;
  ranBoolean: boolean;
  archiveBool: boolean;
  flagged: boolean;
  oldBuildComponents: boolean;
}

export interface BuildDateMetadata {
  assemblyDates: ArrayDate;
  testingDates: ArrayDate;
  buckingDates: ArrayDate;
  redressingDates: ArrayDate[];
  internalTestingDates: ArrayDate[];
}

export interface BuildStatuses {
  assemblyStatus: BUILD_ASSEMBLY_STATUS;
  billingStatus: BUILD_BILLING_STATUS;
  buildStatus: BUILD_BUILDS_STATUS;
  componentStatus: BUILD_COMPONENTS_STATUS;
  locationStatus: BUILD_LOCATION_STATUS;
  shipmentStatus: BUILD_SHIPMENT_STATUS;
  status: string;
}
export interface Loadout {
  projectNumber: string | null;
  jobNumber: string | null;
  shippingContainer: string | null;
  outDate: Date | null;
  inDate: Date | null;
  status: string | null;
}

export enum BUILD_ASSEMBLY_STATUS {
  NONE = "NONE",
  SELECT_PARTS = "SELECT PARTS",
  COMPONENT_SELECTION_NEEDED = "COMPONENT SELECTION NEEDED",
  MANUFACTORING = "MANUFACTORING",
  INSPECTION_PROGRAM = "INSPECTION PROGRAM",
  NEED_APPROVAL = "NEED APPROVAL",
  RFP = "RFP",
  PULLED = "PULLED",
  RFA = "RFA",
  ASSEMBLING = "ASSEMBLING",
  ASSEMBLED = "ASSEMBLED",
  NEEDS_REDRESSING = "NEEDS REDRESSING",
  REDRESSING = "REDRESSING",
  REDRESSED = "REDRESSED",
  DISASSEMBLING = "DISASSEMBLING",
  DISASSEMBLED = "DISASSEMBLED",
}

export enum BUILD_BUILDS_STATUS {
  NONE = "NONE",
  CREATED = "CREATED",
  PLANNING = "PLANNING",
  INPROCESS = "INPROCESS",
  IN_PROCESS = "IN PROCESS",
  ON_HOLD = "ON HOLD",
  COMPLETE = "COMPLETE",
  VOID = "VOID",
  TRANSFERRED = "TRANSFERRED",
  QUARANTINED = "QUARANTINED",
}

export enum BUILD_BILLING_STATUS {
  NONE = "NONE",
  QUOTED = "QUOTED",
  INVOICED = "INVOICED",
  PAID = "PAID",
  NO_INVOICE = "NO INVOICE",
}

export enum BUILD_COMPONENTS_STATUS {
  NONE = "NONE",
  ORDERED = "ORDERED",
  RECEIVED = "RECEIVED",
  DAMAGED = "DAMAGED",
  INSPECTED = "INSPECTED",
}

export enum BUILD_LOCATION_STATUS {
  NONE = "NONE",
  DIS_LFT = "DIS LFT",
  TRANSIT = "TRANSIT",
  ON_SITE = "ON SITE",
  VENDOR = "VENDOR",
  CUSTOMER = "CUSTOMER",
  UNKNOWN = "UNKNOWN",
}

export enum BUILD_SHIPMENT_STATUS {
  NONE = "NONE",
  ASSIGNED = "ASSIGNED",
  RTS = "RTS",
  SHIPPED = "SHIPPED",
  RECEIVED = "RECEIVED",
  DOWNHOLE = "DOWNHOLE",
  LIH = "LIH",
  LOST = "LOST",
  IN_CUST_POS = "IN CUST POS",
  CANCELLED = "CANCELLED",
}

/**
 * `StagedGroupType.code` values from the SQL Connect `BuildStage` relational model — the
 * current staging system, replacing the old hardcoded `stagedBuildIds` job-uid approach above.
 */
export enum STAGED_GROUP_TYPE_CODE {
  STAGED_RENTAL = "STAGED_RENTAL",
  CUSTOMER_INVENTORY = "CUSTOMER_INVENTORY",
}

export const BUILD_ICON = {
  ASSEMBLING: "flex_direction",
  ASSEMBLED: "list_alt_check",
  ASSIGNED: "assignment_turned_in",
  BUILD: "build",
  COMPLETE: "priority",
  CREATED: "add_circle",
  CUSTOMER: "domain",
  DAMAGED: "destruction",
  DIS_LFT: "home",
  DOWNHOLE: "oil_barrel",
  IN_CUST_POS: "for_you",
  INSPECTED: "rubric",
  INSPECTION_PROGRAM: "rubric",
  INVOICED: "paid",
  LIH: "oil_barrel",
  LOST: "help",
  MANUFACTORING: "factory",
  NONE: "block",
  NO_INVOICE: "money_off",
  ON_SITE: "domain",
  ORDERED: "box",
  PAID: "paid",
  PULLED: "approval_delegation",
  QUOTED: "request_quote",
  RECEIVED: "call_received",
  REDRESSING: "tv_options_edit_channels",
  REDRESSED: "tv_options_edit_channels",
  RFA: "view_list",
  RFP: "trackpad_input",
  RTS: "local_shipping",
  SHIPPED: "local_shipping",
  TESTING: "water_pump",
  TRANSIT: "airport_shuttle",
  VENDOR: "domain",
};

export const BUILD_PIPLINE_STATUSES = {
  ASSIGNED: {
    icon: BUILD_ICON.ASSIGNED,
    iconColor: "black",
    statusColor: "gold",
  },
};
